import React from 'react'
import { List, Button, Empty, Tag, Typography, Space } from 'antd'
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useNotificationStore, NotificationItem } from '@/stores/notificationStore'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/pt-br'

dayjs.extend(relativeTime)

const levelConfig: Record<string, { color: string; icon: React.ReactNode }> = {
  info: { color: 'blue', icon: <InfoCircleOutlined /> },
  success: { color: 'green', icon: <CheckCircleOutlined /> },
  warning: { color: 'orange', icon: <WarningOutlined /> },
  error: { color: 'red', icon: <CloseCircleOutlined /> },
}

const NotificationPanel: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const { notifications, markAsRead, markAllAsRead, removeNotification, clearAll } = useNotificationStore()

  const dayjsLocale = i18n.language === 'zh-CN' ? 'zh-cn' : i18n.language === 'pt-BR' ? 'pt-br' : 'en'

  return (
    <div style={{ width: 360 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Typography.Text strong>{t('notification.title')}</Typography.Text>
        <Space size="small">
          <Button type="link" size="small" onClick={markAllAsRead}>
            {t('notification.markAllRead')}
          </Button>
          <Button type="link" size="small" danger onClick={clearAll}>
            {t('notification.clearAll')}
          </Button>
        </Space>
      </div>
      <div style={{ maxHeight: 400, overflow: 'auto' }}>
        {notifications.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('notification.empty')} style={{ padding: '24px 0' }} />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(item: NotificationItem) => {
              const cfg = levelConfig[item.level] || levelConfig.info
              return (
                <List.Item
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    background: item.read ? 'transparent' : '#f6f8ff',
                  }}
                  onClick={() => markAsRead(item.id)}
                  extra={
                    <Button type="text" size="small" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); removeNotification(item.id) }} />
                  }
                >
                  <List.Item.Meta
                    avatar={<span style={{ color: cfg.color, fontSize: 18 }}>{cfg.icon}</span>}
                    title={
                      <Space size={4}>
                        <span style={{ fontSize: 13 }}>{t(item.titleKey, item.params)}</span>
                        <Tag color={cfg.color} style={{ fontSize: 11, lineHeight: '18px', marginRight: 0 }}>{t(`notification.level.${item.level}`)}</Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div style={{ fontSize: 12, color: '#666' }}>{t(item.bodyKey, item.params)}</div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{dayjs(item.timestamp).locale(dayjsLocale).fromNow()}</div>
                      </div>
                    }
                  />
                </List.Item>
              )
            }}
          />
        )}
      </div>
    </div>
  )
}

export default NotificationPanel
